import { useState, useEffect } from 'react'
import useAuth from '../../hooks/useAuth'
import PageLayout from '../common/layout/PageLayout'
import ApiAwards from '../../api/ApiAwards'
import toastr from 'toastr'

function AdminAwardDefinitions() {
  const { isAdmin, loading: authLoading } = useAuth()
  const [loading, setLoading] = useState(false)
  const [awards, setAwards] = useState([])
  const [showForm, setShowForm] = useState(false)
  const [editingAward, setEditingAward] = useState(null)
  const [uploadingIcon, setUploadingIcon] = useState(false)

  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    category: 'achievement',
    icon_key: 'trophy',
    custom_icon_url: '',
    display_order: 0,
    is_active: true,
  })

  const iconOptions = [
    { value: 'trophy', label: '🏆 Trophy' },
    { value: 'medal', label: '🏅 Medal' },
    { value: 'star', label: '⭐ Star' },
    { value: 'calendar', label: '📅 Calendar' },
    { value: 'trending-up', label: '📈 Trending Up' },
    { value: 'heart', label: '❤️ Heart' },
    { value: 'zap', label: '⚡ Zap' },
    { value: 'refresh', label: '🔄 Refresh' },
    { value: 'check-circle', label: '✅ Check Circle' },
  ]

  const categoryOptions = [
    { value: 'achievement', label: 'Achievement' },
    { value: 'performance', label: 'Performance' },
    { value: 'participation', label: 'Participation' },
    { value: 'special', label: 'Special' },
    { value: 'trophy', label: 'Trophy' },
    { value: 'bag_tag', label: 'Bag Tag' },
  ]

  useEffect(() => {
    if (isAdmin) {
      loadAwards()
    }
  }, [isAdmin])

  const loadAwards = async () => {
    try {
      const result = await ApiAwards.getAllDefinitions()
      if (result.success && result.data?.definitions) {
        setAwards(result.data.definitions)
      }
    } catch {
      toastr.error('Failed to load awards')
    }
  }

  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      description: '',
      category: 'achievement',
      icon_key: 'trophy',
      custom_icon_url: '',
      display_order: 0,
      is_active: true,
    })
    setEditingAward(null)
    setShowForm(false)
  }

  const handleEdit = (award) => {
    setEditingAward(award)
    setFormData({
      code: award.code,
      name: award.name,
      description: award.description || '',
      category: award.category,
      icon_key: award.icon_key || 'trophy',
      custom_icon_url: award.custom_icon_url || '',
      display_order: award.display_order || 0,
      is_active: award.is_active !== false,
    })
    setShowForm(true)
  }

  const handleIconUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toastr.error('Please select an image file')
      return
    }

    // Validate file size (2MB limit)
    if (file.size > 2 * 1024 * 1024) {
      toastr.error('File size must be less than 2MB')
      return
    }

    setUploadingIcon(true)
    try {
      const result = await ApiAwards.uploadIcon(file)
      if (result.success && result.data?.icon_url) {
        setFormData({ ...formData, custom_icon_url: result.data.icon_url })
        toastr.success('Icon uploaded successfully')
      } else {
        toastr.error(result.error || 'Failed to upload icon')
      }
    } catch {
      toastr.error('Error uploading icon')
    } finally {
      setUploadingIcon(false)
      // Clear the file input to avoid showing a stale selected file
      if (e && e.target) {
        e.target.value = ''
      }
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!formData.code || !formData.name || !formData.category) {
      toastr.warning('Please fill in all required fields')
      return
    }

    setLoading(true)
    try {
      let result
      if (editingAward) {
        // Update existing award - only send allowed fields
        // eslint-disable-next-line no-unused-vars
        const { code, ...updates } = formData
        result = await ApiAwards.updateDefinition({
          id: editingAward.id,
          ...updates,
        })
      } else {
        // Create new award - explicitly pass expected fields
        result = await ApiAwards.createDefinition({
          code: formData.code,
          name: formData.name,
          description: formData.description,
          category: formData.category,
          icon_key: formData.icon_key,
          custom_icon_url: formData.custom_icon_url,
          display_order: formData.display_order,
          is_active: formData.is_active,
        })
      }

      if (result.success) {
        toastr.success(
          editingAward
            ? 'Award updated successfully'
            : 'Award created successfully',
        )
        resetForm()
        loadAwards()
      } else {
        toastr.error(result.error || 'Failed to save award')
      }
    } catch (err) {
      toastr.error(err.message || 'Error saving award')
    } finally {
      setLoading(false)
    }
  }

  if (authLoading) return <p>Loading...</p>
  if (!isAdmin) return <p>Access denied. You are not an admin.</p>

  return (
    <PageLayout title="Manage Award Definitions">
      <div className="container py-4">
        <div className="row justify-content-center">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h2>Award Definitions</h2>
              <button
                className="btn btn-primary"
                onClick={() => setShowForm(!showForm)}
              >
                {showForm ? 'Cancel' : '+ Create New Award'}
              </button>
            </div>

            {showForm && (
              <div className="card mb-4">
                <div className="card-header">
                  <h5 className="mb-0">
                    {editingAward ? 'Edit Award' : 'Create New Award'}
                  </h5>
                </div>
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    <div className="row">
                      <div className="col-12 col-md-6 mb-3">
                        <label htmlFor="code" className="form-label">
                          Code <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          id="code"
                          className="form-control"
                          value={formData.code}
                          onChange={(e) =>
                            setFormData({ ...formData, code: e.target.value })
                          }
                          disabled={!!editingAward}
                          readOnly={!!editingAward}
                          required
                        />
                        <div className="form-text">
                          Unique identifier (e.g., most_wins). Cannot be changed
                          after creation.
                        </div>
                      </div>

                      <div className="col-12 col-md-6 mb-3">
                        <label htmlFor="name" className="form-label">
                          Name <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          id="name"
                          className="form-control"
                          value={formData.name}
                          onChange={(e) =>
                            setFormData({ ...formData, name: e.target.value })
                          }
                          required
                        />
                      </div>
                    </div>

                    <div className="mb-3">
                      <label htmlFor="description" className="form-label">
                        Description
                      </label>
                      <textarea
                        id="description"
                        className="form-control"
                        rows="3"
                        value={formData.description}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            description: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div className="row">
                      <div className="col-12 col-md-6 mb-3">
                        <label htmlFor="category" className="form-label">
                          Category <span className="text-danger">*</span>
                        </label>
                        <select
                          id="category"
                          className="form-select"
                          value={formData.category}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              category: e.target.value,
                            })
                          }
                          required
                        >
                          {categoryOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="col-12 col-md-6 mb-3">
                        <label htmlFor="display_order" className="form-label">
                          Display Order
                        </label>
                        <input
                          type="number"
                          id="display_order"
                          className="form-control"
                          value={formData.display_order}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              display_order: parseInt(e.target.value) || 0,
                            })
                          }
                        />
                        <div className="form-text">
                          Lower values appear first
                        </div>
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Icon</label>
                      <div className="row">
                        <div className="col-12 col-md-6 mb-2">
                          <label
                            htmlFor="icon_key"
                            className="form-label small"
                          >
                            Select Icon
                          </label>
                          <select
                            id="icon_key"
                            className="form-select"
                            value={formData.icon_key}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                icon_key: e.target.value,
                              })
                            }
                          >
                            {iconOptions.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="col-12 col-md-6 mb-2">
                          <label
                            htmlFor="custom_icon"
                            className="form-label small"
                          >
                            Or Upload Custom Icon
                          </label>
                          <input
                            type="file"
                            id="custom_icon"
                            className="form-control"
                            accept="image/*"
                            onChange={handleIconUpload}
                            disabled={uploadingIcon}
                          />
                        </div>
                      </div>

                      {formData.custom_icon_url && (
                        <div className="mt-2">
                          <div className="d-flex align-items-center gap-2">
                            <img
                              src={formData.custom_icon_url}
                              alt="Custom icon preview"
                              style={{
                                width: '48px',
                                height: '48px',
                                objectFit: 'contain',
                                border: '1px solid #dee2e6',
                                borderRadius: '4px',
                                padding: '4px',
                              }}
                            />
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() =>
                                setFormData({
                                  ...formData,
                                  custom_icon_url: '',
                                })
                              }
                            >
                              Remove Custom Icon
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="mb-3 form-check">
                      <input
                        type="checkbox"
                        id="is_active"
                        className="form-check-input"
                        checked={formData.is_active}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            is_active: e.target.checked,
                          })
                        }
                      />
                      <label htmlFor="is_active" className="form-check-label">
                        Active (visible to users)
                      </label>
                    </div>

                    <div className="d-flex gap-2">
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={loading || uploadingIcon}
                      >
                        {loading
                          ? 'Saving...'
                          : editingAward
                            ? 'Update Award'
                            : 'Create Award'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={resetForm}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">Existing Awards</h5>
              </div>
              <div className="card-body">
                {awards.length === 0 ? (
                  <p className="text-muted">No awards defined yet.</p>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th>Icon</th>
                          <th>Name</th>
                          <th>Code</th>
                          <th>Category</th>
                          <th>Order</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {awards.map((award) => (
                          <tr key={award.id}>
                            <td>
                              {award.custom_icon_url ? (
                                <>
                                  <img
                                    src={award.custom_icon_url}
                                    alt={award.name}
                                    style={{
                                      width: '32px',
                                      height: '32px',
                                      objectFit: 'contain',
                                    }}
                                    onError={(e) => {
                                      e.target.onerror = null
                                      e.target.style.display = 'none'
                                      const fallback = e.target.nextSibling
                                      if (fallback) {
                                        fallback.style.display = 'inline-block'
                                      }
                                    }}
                                  />
                                  <span
                                    style={{
                                      fontSize: '24px',
                                      display: 'none',
                                    }}
                                  >
                                    {iconOptions
                                      .find((o) => o.value === award.icon_key)
                                      ?.label?.split(' ')[0] || '🎖️'}
                                  </span>
                                </>
                              ) : (
                                <span style={{ fontSize: '24px' }}>
                                  {iconOptions
                                    .find((o) => o.value === award.icon_key)
                                    ?.label?.split(' ')[0] || '🎖️'}
                                </span>
                              )}
                            </td>
                            <td>{award.name}</td>
                            <td>
                              <code>{award.code}</code>
                            </td>
                            <td>{award.category}</td>
                            <td>{award.display_order}</td>
                            <td>
                              <span
                                className={`badge ${award.is_active ? 'bg-success' : 'bg-secondary'}`}
                              >
                                {award.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td>
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => handleEdit(award)}
                              >
                                Edit
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  )
}

export default AdminAwardDefinitions
